import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { PGWheelInfo, PGWheelSection, PGWheelWinInfo, PGWheelWinTrigger } from '../models';
import { RootStore } from '.';
import { launcherSocket, boostedJackpotController } from '../api';
import { soundService, soundOptions } from '../services/soundService';
import { loadResources } from '../jackpot/helpers';
import { getBoostedJackpotResources } from '../jackpot/modules/frameModule/constants';
import { NotificationPriority, PGWheelRewardType, SideMenuIcons } from '../enums';
import { dotFormatter, isMobile, roundNumber } from '../helpers';
import { failLaunch } from '../services/launch-telemetry';
import { T } from '../constants';

export class BoostedJackpotStore {
  private rootStore: RootStore;
  isWheel: boolean = false;
  wheelValues: any[] = [];
  waitBoosted: boolean = false
  lastWinnerDate: string = '';
  sectorStopIndex: number | null = null;
  boostedBetAmount: number | null = null;
  spinWheelWinAmount: number | null = null;
  spinWheelWinIsFreeBet: boolean = false;
  playerBoostedWinAmount: number = 0;
  boostedJackpotEndTime: string = '';
  hasBoostedJackpot: boolean = false;
  wheelInfo: PGWheelInfo | null = null;
  boostedJackpotInfo: any = null;
  isBoostedJackpotWinPopup: boolean = false;
  isBoostedJackpotInfoPopup: boolean = false;
  isBoostedJackpotInitialPopup: boolean = true;
  isBoostedPopupOpen: boolean = false
  replaySpin: boolean = false;
  wheelTriggerExpireDate: string | undefined = undefined;
  isWheelZoomOut: boolean = false;
  boostedJackpotResourcesLoaded: boolean = false;
  openBoostedJackpotAfterRoundFinished: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      isWheel: observable,
      sectorStopIndex: observable,
      boostedBetAmount: observable,
      hasBoostedJackpot: observable,
      wheelValues: observable.deep,
      boostedJackpotInfo: observable,
      spinWheelWinAmount: observable,
      spinWheelWinIsFreeBet: observable,
      setSpinWheelWinIsFreeBet: action.bound,
      boostedJackpotEndTime: observable,
      playerBoostedWinAmount: observable,
      isBoostedPopupOpen: observable,
      isBoostedJackpotWinPopup: observable,
      isBoostedJackpotInfoPopup: observable,
      isBoostedJackpotInitialPopup: observable,
      waitBoosted: observable,
      openBoostedJackpotAfterRoundFinished: observable,
      setOpenBoostedJackpotAfterRoundFinished: action.bound,
      replaySpin: observable,
      wheelTriggerExpireDate: observable,
      setWheelTriggerExpireDate: action.bound,
      setIsWheel: action.bound,
      setSectorStopIndex: action.bound,
      setSpinWheelWinAmount: action.bound,
      setBoostedJackpotInfo: action.bound,
      setHasBoostedJackpot: action.bound,
      setPlayerBoostedWinAmount: action.bound,
      setIsBoostedJackpotWinPopup: action.bound,
      setIsBoostedJackpotInfoPopup: action.bound,
      setIsBoostedJackpotInitialPopup: action.bound,
      updateIsBoostedJackpotInitialPopup: action.bound,
      setReplaySpin: action.bound,
      setIsBoostedPopupOpen: action.bound,
      isWheelZoomOut: observable,
      setIsWheelZoomOut: action.bound,
      wheelInfo: observable,
      setWheelInfo: action.bound,
      boostedJackpotResourcesLoaded: observable,
      setBoostedJackpotResourcesLoaded: action.bound,
      lastWinAmount: computed,
      largestWinAmount: computed,
      showWheelWidget: computed,
      isWheelInFlight: computed,
    });

    reaction(
      () => this.rootStore.gameStore.roundInProgress,
      (inProgress) => {
        if (!inProgress && this.openBoostedJackpotAfterRoundFinished) {
          this.setOpenBoostedJackpotAfterRoundFinished(false);
          setTimeout(() => {
            this.showBoostedJackpotWheel();
          }, 200);
        }
      }
    );
  };

  public get showWheelWidget(): boolean {
    return !isMobile() || !this.rootStore.gameStore.launchData.data?.hasCashback;
  }

  /**
   * A wheel that is already triggered or on screen must survive a hub drop. handleWheelWinTrigger has
   * already called sendPauseGame(), and the only sendResumeGame() lives in the win popup, which cannot
   * open until a result arrives — so unmounting here leaves the game paused with no way out but a
   * reload. The spin result comes over HTTP (boostedJackpotController.spin), so it still works.
   */
  public get isWheelInFlight(): boolean {
    return this.hasBoostedJackpot || this.isBoostedPopupOpen || this.openBoostedJackpotAfterRoundFinished;
  }

  public get sectionsMap(): Record<number, PGWheelSection> {
    if (!this.wheelInfo?.sections) return {};
    return this.wheelInfo.sections.reduce((map, section) => {
      map[section.position] = section;
      return map;
    }, {} as Record<number, PGWheelSection>);
  }

  public get lastWinAmount(): number {
    const section = this.sectionsMap[this.wheelInfo?.lastWinPosition ?? -1];
    if (!section) return 0;
    const amount = section.rewardType === PGWheelRewardType.FreeBet
      ? (section.freeBetCount ?? 0) * section.amount
      : section.amount ?? 0;
    return roundNumber(amount);
  }

  public get largestWinAmount(): number {
    const section = this.sectionsMap[this.wheelInfo?.largestWinPosition ?? -1];
    if (!section) return 0;
    const amount = section.rewardType === PGWheelRewardType.FreeBet
      ? (section.freeBetCount ?? 0) * section.amount
      : section.amount ?? 0;
    return roundNumber(amount);
  }

  setIsBoostedPopupOpen = (state: boolean) => {
    this.isBoostedPopupOpen = state
  }

  setWaitBoosted = (status: boolean) => {
    this.waitBoosted = status
  }

  setWheelInfo = (data: PGWheelInfo | null) => {
    this.wheelInfo = data;
  }

  setWheelTriggerExpireDate = (date: string | undefined) => {
    this.wheelTriggerExpireDate = date;
  }

  setIsWheel(isWheel: boolean) {
    this.isWheel = isWheel;
  };

  setSectorStopIndex(sectorStopIndex: number | null) {
    this.sectorStopIndex = sectorStopIndex;
  };

  setPlayerBoostedWinAmount(playerBoostedWinAmount: number) {
    this.playerBoostedWinAmount = playerBoostedWinAmount;
  };

  setHasBoostedJackpot = (hasBoostedJackpot: boolean) => {
    this.hasBoostedJackpot = hasBoostedJackpot;
  };

  setSpinWheelWinAmount(spinWheelWinAmount: number) {
    this.spinWheelWinAmount = spinWheelWinAmount;
  };

  setSpinWheelWinIsFreeBet(value: boolean) {
    this.spinWheelWinIsFreeBet = value;
  };

  setIsBoostedJackpotWinPopup(isBoostedJackpotWinPopup: boolean) {
    if (!isBoostedJackpotWinPopup && this.isBoostedJackpotWinPopup) {
      soundService.stopAll();
    }
    this.isBoostedJackpotWinPopup = isBoostedJackpotWinPopup;
  };

  setIsBoostedJackpotInfoPopup(isBoostedJackpotInfoPopup: boolean) {
    this.isBoostedJackpotInfoPopup = isBoostedJackpotInfoPopup;
  };

  setReplaySpin = (status: boolean) => {
    this.replaySpin = status
  }

  setIsWheelZoomOut(value: boolean) {
    this.isWheelZoomOut = value;
  }

  setOpenBoostedJackpotAfterRoundFinished(value: boolean) {
    this.openBoostedJackpotAfterRoundFinished = value;
  }

  setBoostedJackpotInfo(data: PGWheelInfo) {
    this.setWheelInfo(data);
    this.wheelValues = data.sections;
    this.boostedBetAmount = data.minBetAmount;
    this.boostedJackpotEndTime = data.endDate;
  };

  updateIsBoostedJackpotInitialPopup() {
    this.isBoostedJackpotInitialPopup = !this.isBoostedJackpotInitialPopup;
  };

  setIsBoostedJackpotInitialPopup(isBoostedJackpotInitialPopup: boolean) {
    this.isBoostedJackpotInitialPopup = isBoostedJackpotInitialPopup;
  };

  handleBoostedJackpotSpinResult(data: PGWheelInfo) {
    const rawPosition = Number((data as any)?.position);
    const section = this.sectionsMap[rawPosition];
    const isFreeBet = section.rewardType === PGWheelRewardType.FreeBet;
    const winAmount = isFreeBet
      ? (section.freeBetCount ?? 0) * section?.amount
      : section?.amount;
    this.setSpinWheelWinAmount(winAmount);
    this.setSpinWheelWinIsFreeBet(isFreeBet);

    if (Number.isFinite(rawPosition)) {
      const sorted = [...this.wheelValues].sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
      const idx = sorted.findIndex((s: any) => Number(s.position) === rawPosition);
      if (idx !== -1) {
        this.setSectorStopIndex(idx);
      }
    }
  }

  handleWheelWinTrigger(data: PGWheelWinTrigger) {
    this.rootStore.postMessageStore.sendPauseGame();
    this.setWheelTriggerExpireDate(data?.wheelExpireDate);
    if (this.rootStore.gameStore.roundInProgress) {
      this.setOpenBoostedJackpotAfterRoundFinished(true);
    } else {
      this.showBoostedJackpotWheel();
    }
    if (data.wheelWinId) {
      // send() no-ops when the socket is not Connected, so a dropped ack is invisible today.
      const acked = launcherSocket.sendAckWheelWin(data.wheelWinId);
      if (!acked) failLaunch('wheel_ack_dropped', String(data.wheelWinId));
    }
  }

  showBoostedJackpotWheel() {
    this.setHasBoostedJackpot(true);
    this.setIsBoostedPopupOpen(true);
    this.setIsWheel(true);
  }

  handleWheelWinInfo(data: PGWheelWinInfo) {
    const {
      lastWinDate,
      remainingWinsCount,
      position,
      winId,
      largestWinPosition,
      largestWinDate,

    } = data;

    const wheelWinAmount = this.sectionsMap[position].rewardType === PGWheelRewardType.FreeBet
      ? (this.sectionsMap[position].freeBetCount ?? 0) * this.sectionsMap[position]?.amount
      : this.sectionsMap[position]?.amount ?? 0;
    this.setPlayerBoostedWinAmount(wheelWinAmount);

    const { currencySymbol } = this.rootStore.gameStore;
    if (!this.showWheelWidget) {
      this.rootStore.sidebarMenuStore.setHasPromotionsNotification(true);
      this.rootStore.sidebarNotificationStore.enqueue(
        `${this.rootStore.translationsStore.translate(T.LAST_PLAYER_HAS_WON)} ${dotFormatter(wheelWinAmount, 2)} ${currencySymbol}`,
        SideMenuIcons.Wheel,
        NotificationPriority.High
      );
    }

    if (this.wheelInfo) {

      const remainingPoolAmount = (!this.wheelInfo.lastWinId || winId > this.wheelInfo.lastWinId) ? roundNumber(this.wheelInfo.remainingPoolAmount - wheelWinAmount) : this.wheelInfo.remainingPoolAmount;

      this.setWheelInfo({
        ...this.wheelInfo,
        lastWinDate,
        largestWinDate,
        lastWinId: winId,
        largestWinPosition,
        remainingPoolAmount,
        lastWinPosition: position,
        remainingWinCount: remainingWinsCount,
      });
    }
  }

  handleWheelWidgetEnded() {
    this.setWheelInfo(null);
    this.setIsWheelZoomOut(false);
    this.setIsBoostedJackpotWinPopup(false);
    this.setSectorStopIndex(null as any);
    this.setWheelTriggerExpireDate(undefined);
  }

  async handleWheelWidgetStarted(data: PGWheelInfo) {
    this.setWheelTriggerExpireDate(data?.wheelExpireDate);

    if (!this.boostedJackpotResourcesLoaded) {
      await this.loadBoostedJackpotResources();
    }

    const wheelId = data?.wheelId;
    if (!wheelId) { return null }

    const response = await boostedJackpotController.getWheelInfo(wheelId);
    const wheelInfo = response?.result;
    if (!wheelInfo) return;

    this.setBoostedJackpotInfo({
      ...wheelInfo,
      wheelExpireDate: data?.wheelExpireDate,
    });
    this.setIsBoostedJackpotInitialPopup(true);
  }

  setBoostedJackpotResourcesLoaded(value: boolean) {
    this.boostedJackpotResourcesLoaded = value;
  }

  loadBoostedJackpotResources = async (): Promise<void> => {
    const { partnerId } = this.rootStore.gameStore;

    const resources = getBoostedJackpotResources(partnerId);

    await loadResources(resources);
    soundService.setSoundResources(soundOptions);
    await soundService.loadSoundResources();
    this.setBoostedJackpotResourcesLoaded(true);
  };

  async getBoostedJackpot(): Promise<void> {
    const gameId = this.rootStore.gameStore.gameId;
    const wheelId = this.wheelInfo?.wheelId;

    if (!gameId || !wheelId) {
      console.warn('[BoostedJackpotStore] Missing gameId or wheelId for boosted jackpot spin request.');
      return;
    }

    try {
      const response = await boostedJackpotController.spin({ wheelId, gameId });
      const result = response?.result as any;

      if (result && Number.isFinite(Number(result.position))) {
        this.handleBoostedJackpotSpinResult(result);
      }
    } catch (error) {
      console.error('[BoostedJackpotStore] Failed to request boosted jackpot spin result.', error);
    }
  }

  public listenEvents(): void {
    launcherSocket.onWheelWinTrigger(this.handleWheelWinTrigger.bind(this));
    launcherSocket.onWheelWinInfo(this.handleWheelWinInfo.bind(this));
    launcherSocket.onWheelWidgetEnded(this.handleWheelWidgetEnded.bind(this));
    launcherSocket.onWheelWidgetStarted(this.handleWheelWidgetStarted.bind(this));
  }
};